import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Truck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AuthImage } from "@/components/AuthImage";

export const due30 = (d) => {
  const days = (new Date(d) - Date.now()) / 86400000;
  return days < 0 ? "font-bold text-red-600" : days <= 30 ? "font-bold text-amber-600" : "text-muted-foreground";
};
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, Empty, DemoBadge } from "@/components/common";
import { fmtD } from "@/lib/format";
import { cn } from "@/lib/utils";

export const ASSET_TONE = { disponibile: "bg-emerald-100 text-emerald-800", assegnato: "bg-sky-100 text-sky-800", guasto: "bg-red-600 text-white", manutenzione: "bg-amber-100 text-amber-800", fuori_servizio: "bg-zinc-200 text-zinc-600" };

export const useAssetMeta = () => {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/assets/meta").then((r) => setM(r.data));
  }, []);
  return m;
};

const EMPTY = { kind: "mezzo", category: "autocarro", code: "", brand: "", model: "", serial: "", location: "", commessa_id: "", next_maintenance_at: "", revision_due: "", insurance_due: "", required_qualification: "", external_id_drflotta: "", notes: "" };
const DATES = ["next_maintenance_at", "revision_due", "insurance_due"];
const fromAsset = (a) => ({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, a[k] ?? ""])), ...Object.fromEntries(DATES.map((k) => [k, a[k] ? a[k].slice(0, 10) : ""])) });

export const AssetFormDialog = ({ open, onOpenChange, meta, onSaved, asset }) => {
  const [commesse, setCommesse] = useState([]);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    if (open) {
      api.get("/commesse").then((r) => setCommesse(r.data));
      setF(asset ? fromAsset(asset) : EMPTY);
    }
  }, [open, asset]);
  const save = async () => {
    const body = { ...f, commessa_id: f.commessa_id || null, ...Object.fromEntries(DATES.map((k) => [k, f[k] ? new Date(`${f[k]}T09:00:00`).toISOString() : null])) };
    try {
      const { data } = asset ? await api.put(`/assets/${asset.id}`, { ...body, version: asset.version }) : await api.post("/assets", body);
      toast.success(asset ? "Scheda aggiornata" : "Registrato in inventario");
      onOpenChange(false);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const txt = (k, label) => <div className="space-y-1"><Label>{label}</Label><Input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`asset-form-${k}`} /></div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[92vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="asset-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{asset ? `Modifica scheda · ${asset.code}` : "Nuovo mezzo / attrezzatura"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label>Tipo</Label><NativeSelect className="w-full" testid="asset-form-kind" value={f.kind} onChange={(v) => set("kind", v)}><option value="mezzo">Mezzo</option><option value="attrezzatura">Attrezzatura</option></NativeSelect></div>
          <div className="space-y-1"><Label>Categoria</Label><NativeSelect className="w-full" testid="asset-form-category" value={f.category} onChange={(v) => set("category", v)}>{Object.entries(meta.categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></div>
          {txt("code", "Codice *")}{txt("serial", "Matricola / targa")}{txt("brand", "Marca")}{txt("model", "Modello")}{txt("location", "Ubicazione")}
          <div className="space-y-1"><Label>Commessa</Label><NativeSelect className="w-full" testid="asset-form-commessa" value={f.commessa_id} onChange={(v) => set("commessa_id", v)}><option value="">—</option>{commesse.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>Prossima manutenzione</Label><Input type="date" value={f.next_maintenance_at} onChange={(e) => set("next_maintenance_at", e.target.value)} data-testid="asset-form-next" /></div>
          <div className="space-y-1"><Label>Scadenza revisione</Label><Input type="date" value={f.revision_due} onChange={(e) => set("revision_due", e.target.value)} data-testid="asset-form-revision" /></div>
          <div className="space-y-1"><Label>Scadenza assicurazione</Label><Input type="date" value={f.insurance_due} onChange={(e) => set("insurance_due", e.target.value)} data-testid="asset-form-insurance" /></div>
          {txt("required_qualification", "Abilitazione richiesta")}{txt("external_id_drflotta", "ID DR FLOTTA (integrazione futura)")}
          <div className="space-y-1 sm:col-span-2"><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="asset-form-notes" /></div>
        </div>
        <DialogFooter><Button disabled={f.code.length < 2} onClick={save} className="rounded-xl font-bold" data-testid="asset-form-submit">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Mezzi() {
  const { user } = useAuth();
  const meta = useAssetMeta();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const params = Object.fromEntries(sp.entries());
  const load = useCallback(() => {
    api.get("/assets", { params: Object.fromEntries(sp.entries()) }).then((r) => setRows(r.data));
  }, [sp]);
  useEffect(() => {
    load();
  }, [load]);
  const setParam = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  return (
    <div data-testid="mezzi-page">
      <PageHeader title="Mezzi e attrezzature" subtitle={rows ? `${rows.length} elementi in inventario` : ""}
        actions={["ceo", "responsabile", "manutenzione"].includes(user.role) && <Button onClick={() => setOpen(true)} className="h-11 rounded-xl font-bold" data-testid="mezzi-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo</Button>} />
      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex">
        <NativeSelect testid="mezzi-filter-kind" value={params.kind} onChange={(v) => setParam("kind", v)}><option value="">Mezzi e attrezzature</option><option value="mezzo">Solo mezzi</option><option value="attrezzatura">Solo attrezzature</option></NativeSelect>
        <NativeSelect testid="mezzi-filter-status" value={params.status} onChange={(v) => setParam("status", v)}><option value="">Ogni stato</option>{meta && Object.entries(meta.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect>
        <NativeSelect testid="mezzi-filter-extra" value={params.maintenance ? "m" : params.possession ? "p" : ""} onChange={(v) => { const n = new URLSearchParams(sp); n.delete("maintenance"); n.delete("possession"); if (v === "m") n.set("maintenance", "overdue"); if (v === "p") n.set("possession", "pending"); setSp(n); }}>
          <option value="">Tutti</option><option value="m">Manutenzione scaduta</option><option value="p">Possesso da confermare</option></NativeSelect>
      </div>
      {rows?.length === 0 && <Empty text="Nessun elemento." testid="mezzi-empty" />}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows?.map((a) => (
          <Link key={a.id} to={`/mezzi/${a.id}`} className={cn("card-soft tile-press block overflow-hidden p-0", a.status === "guasto" && "ring-2 ring-red-500")} data-testid={`asset-card-${a.code}`}>
            {a.cover_photo_id && <AuthImage fileId={a.cover_photo_id} alt={a.code} className="h-36 w-full" testid={`asset-card-photo-${a.code}`} />}
            <div className="p-4">
            <div className="flex items-center gap-2">
              <div className="icon-chip h-10 w-10">{a.kind === "mezzo" ? <Truck className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}</div>
              <div className="min-w-0 flex-1"><p className="font-mono text-sm font-bold">{a.code} <DemoBadge show={a.is_demo} /></p><p className="truncate text-sm">{a.brand} {a.model}</p></div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${ASSET_TONE[a.status]}`}>{meta?.statuses[a.status]}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{a.commessa_code || "—"} · {a.assignee_name ? `In custodia a ${a.assignee_name}` : a.location}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className={a.maintenance_overdue ? "font-bold text-red-600" : "text-muted-foreground"}>Manutenzione: {fmtD(a.next_maintenance_at)}</span>
              {a.possession_pending && <span className="rounded-full bg-amber-100 px-2 font-semibold text-amber-800">Possesso da confermare</span>}
              {a.revision_due && <span className={due30(a.revision_due)}>Revisione: {fmtD(a.revision_due)}</span>}
              {a.insurance_due && <span className={due30(a.insurance_due)}>Assicurazione: {fmtD(a.insurance_due)}</span>}
            </div>
            </div>
          </Link>
        ))}
      </div>
      {meta && <AssetFormDialog open={open} onOpenChange={setOpen} meta={meta} onSaved={load} />}
    </div>
  );
}
